import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Toolbar,
  CircularProgress,
  Box,
  Popover,
  Paper,
  MenuList,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  Dashboard,
  People,
  AttachFile,
  ExpandLess,
  ExpandMore,
  Group,
  Rule,
  ConfirmationNumber,
  Settings,
  HowToReg,
  ManageAccounts,
  BusinessCenter,
  ShoppingCart,
  ChevronRight,
  LocalPharmacy,
  AccountCircle,
  History,
} from '@mui/icons-material';
import { useState, useMemo, useEffect, MouseEvent, memo } from 'react';
import { usePermission } from '@/hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { debugLogger } from '@/utils/debugLogger';

export const DRAWER_WIDTH = 260;
export const MINI_DRAWER_WIDTH = 64;

interface MenuItem {
  title: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  requirePermission?: (permissions: ReturnType<typeof usePermission>) => boolean;
}

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

/**
 * 侧边栏组件
 * 使用 memo 优化性能，避免不必要的重渲染
 */
function Sidebar({ open, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverItem, setPopoverItem] = useState<MenuItem | null>(null);
  const permissions = usePermission();
  const { t } = useTranslation();

  // Get dashboard path based on user type
  const getDashboardPath = () => {
    if (permissions.isSupplier()) return '/dashboard/supplier';
    if (permissions.isCustomer()) return '/dashboard/customer';
    return '/dashboard'; // admin or default
  };

  // Get ticket path based on user type
  const getTicketPath = () => {
    if (permissions.isAdmin()) return '/tickets/admin';
    if (permissions.isSupplier()) return '/tickets/supplier';
    if (permissions.isCustomer()) return '/tickets/customer';
    return '/tickets/customer'; // default
  };

  // Get profile path based on user type
  const getProfilePath = () => {
    if (permissions.isSupplier()) return '/profile/supplier';
    if (permissions.isCustomer()) return '/profile/customer';
    return '/profile'; // admin or default
  };

  const menuItems: MenuItem[] = [
    {
      title: t('nav.dashboard'),
      icon: <Dashboard />,
      path: getDashboardPath(),
    },
    {
      title: t('nav.systemManagement'),
      icon: <Settings />,
      requirePermission: (p) => p.canAccessSystemManagement() || p.canAccessUserList(),
      children: [
        {
          title: t('nav.userList'),
          icon: <People />,
          path: '/system/users',
          requirePermission: (p) => p.canAccessUserList(),
        },
        {
          title: t('nav.userGroups'),
          icon: <Group />,
          path: '/system/roles',
          requirePermission: (p) => p.canAccessRoles(),
        },
        {
          title: t('nav.permissionRoutes'),
          icon: <Rule />,
          path: '/system/permissions',
          requirePermission: (p) => p.canAccessPermissions(),
        },
        {
          title: t('nav.attachmentManagement'),
          icon: <AttachFile />,
          path: '/system/attachments',
          requirePermission: (p) => p.canAccessAttachments(),
        },
        {
          title: t('nav.operationLogs'),
          icon: <History />,
          path: '/system/operation-logs',
          requirePermission: (p) => p.isAdmin(),
        },
      ],
    },
    {
      title: t('nav.userManagement'),
      icon: <ManageAccounts />,
      requirePermission: (p) => p.canAccessApproval() || p.canAccessUserList(),
      children: [
        {
          title: t('nav.pendingApproval'),
          icon: <HowToReg />,
          path: '/approval/list',
          requirePermission: (p) => p.canAccessApproval(),
        },
        {
          title: t('nav.suppliersList'),
          icon: <BusinessCenter />,
          path: '/system/suppliers',
          requirePermission: (p) => p.canAccessUserList(),
        },
        {
          title: t('nav.customersList'),
          icon: <ShoppingCart />,
          path: '/system/customers',
          requirePermission: (p) => p.canAccessUserList(),
        },
      ],
    },
    {
      title: t('nav.myTickets'),
      icon: <ConfirmationNumber />,
      path: getTicketPath(),
      requirePermission: (p) => p.canAccessCustomerTickets() || p.canAccessSupplierTickets() || p.canAccessAdminTickets(),
    },
    {
      title: t('product.productLibrary'),
      icon: <LocalPharmacy />,
      requirePermission: (p) => p.isCustomer() || p.isSupplier() || p.isAdmin(),
      children: [
        {
          title: t('product.library'),
          icon: <LocalPharmacy />,
          path: '/products/library',
          requirePermission: (p) => p.isCustomer() || p.isSupplier() || p.isAdmin(),
        },
        {
          title: t('product.approval'),
          icon: <HowToReg />,
          path: '/products/approval',
          requirePermission: (p) => p.canAccessDrugApproval(),
        },
      ],
    },
    {
      title: t('nav.profile'),
      icon: <AccountCircle />,
      path: getProfilePath(),
      // All authenticated users can access profile
    },
  ];

  // Auto-expand parent menu based on current path (only in expanded mode)
  useEffect(() => {
    // Only auto-expand when sidebar is open
    if (!open) return;

    const findParentMenu = (items: MenuItem[], currentPath: string): string | null => {
      for (const item of items) {
        if (item.children) {
          // Check if any child matches the current path
          const hasActiveChild = item.children.some(child => child.path === currentPath);
          if (hasActiveChild) {
            return item.title;
          }
          // Recursively check nested children
          const nestedParent = findParentMenu(item.children, currentPath);
          if (nestedParent) {
            return item.title;
          }
        }
      }
      return null;
    };

    const parentMenu = findParentMenu(menuItems, pathname);
    if (parentMenu && !expandedItems.includes(parentMenu)) {
      setExpandedItems(prev => [...prev, parentMenu]);
    }
  }, [pathname, open]);

  // Filter menu items based on permissions
  const filteredMenuItems = useMemo(() => {
    debugLogger.log('🔍 [Sidebar] Filtering menu items...');
    debugLogger.log(`🔍 [Sidebar] Permissions loading: ${permissions.loading}`);
    debugLogger.log(`🔍 [Sidebar] User:`, permissions.user);
    debugLogger.log(`🔍 [Sidebar] isSupplier: ${permissions.isSupplier()}`);
    debugLogger.log(`🔍 [Sidebar] isCustomer: ${permissions.isCustomer()}`);
    debugLogger.log(`🔍 [Sidebar] isAdmin: ${permissions.isAdmin()}`);

    if (permissions.loading) {
      debugLogger.log('⏳ [Sidebar] Permissions still loading, returning empty array');
      return [];
    }

    const filterItems = (items: MenuItem[]): MenuItem[] => {
      return items
        .map((item) => {
          // If item has children, filter them first
          if (item.children) {
            const filteredChildren = filterItems(item.children);
            // Only show parent if it has visible children
            if (filteredChildren.length === 0) {
              debugLogger.log(`❌ [Sidebar] Hiding parent "${item.title}" - no visible children`);
              return null;
            }
            debugLogger.log(`✅ [Sidebar] Showing parent "${item.title}" with ${filteredChildren.length} children`);
            return { ...item, children: filteredChildren };
          }

          // For items without children, check permission
          if (!item.requirePermission) {
            debugLogger.log(`✅ [Sidebar] Showing "${item.title}" - no permission required`);
            return item;
          }
          const hasPermission = item.requirePermission(permissions);
          debugLogger.log(`${hasPermission ? '✅' : '❌'} [Sidebar] "${item.title}" - permission: ${hasPermission}`);
          return hasPermission ? item : null;
        })
        .filter((item): item is MenuItem => item !== null);
    };

    const filtered = filterItems(menuItems);
    debugLogger.log(`🔍 [Sidebar] Filtered menu count: ${filtered.length}`);
    return filtered;
  }, [permissions]);

  const handleItemClick = (item: MenuItem) => {
    if (item.children && open) {
      // 展开状态：切换父菜单的展开/收缩状态
      setExpandedItems((prev) =>
        prev.includes(item.title)
          ? prev.filter((title) => title !== item.title)
          : [...prev, item.title]
      );
    } else if (item.path) {
      // 点击子菜单项时，只导航，不影响父菜单状态
      navigate(item.path);
      handlePopoverClose();
    }
  };

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>, item: MenuItem) => {
    if (!open && item.children) {
      setAnchorEl(event.currentTarget);
      setPopoverItem(item);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverItem(null);
  };

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    const isExpanded = expandedItems.includes(item.title);
    const isActive = item.path === pathname;

    // Mini模式下的渲染
    if (!open && depth === 0) {
      return (
        <ListItem key={item.title} disablePadding>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            onMouseEnter={(e) => handlePopoverOpen(e, item)}
            selected={isActive}
            sx={{
              minHeight: 48,
              justifyContent: 'center',
              px: 2.5,
              py: 1.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                color: isActive ? 'primary.main' : 'inherit',
              }}
            >
              {item.icon}
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      );
    }

    // 展开模式下的渲染
    return (
      <div key={item.title}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            selected={isActive}
            sx={{
              pl: 2 + depth * 2,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: isActive ? 'primary.main' : 'inherit',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.title}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 500,
                color: isActive ? 'primary.main' : 'text.primary',
                noWrap: true,
                sx: {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
            {item.children &&
              (isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
          </ListItemButton>
        </ListItem>

        {item.children && open && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </div>
    );
  };

  return (
    <>
      <Drawer
        variant="persistent"
        anchor="left"
        open={true}
        onClose={onClose}
        sx={{
          width: open ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
            boxSizing: 'border-box',
            transition: 'width 0.3s ease-in-out',
            overflowX: 'hidden',
          },
        }}
      >
        <Toolbar />
        {permissions.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>{filteredMenuItems.map((item) => renderMenuItem(item))}</List>
        )}
      </Drawer>

      {/* Popover for submenu in mini mode */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          pointerEvents: 'none',
        }}
        PaperProps={{
          onMouseEnter: () => {
            // Keep popover open when mouse enters
            if (anchorEl) {
              const paper = document.querySelector('.MuiPopover-paper');
              if (paper) {
                (paper as HTMLElement).style.pointerEvents = 'auto';
              }
            }
          },
          onMouseLeave: handlePopoverClose,
          sx: {
            pointerEvents: 'auto',
            ml: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          },
        }}
      >
        {popoverItem && popoverItem.children && (
          <MenuList sx={{ py: 1 }}>
            <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {popoverItem.title}
              </Typography>
            </Box>
            {popoverItem.children.map((child) => {
              const isActive = child.path === pathname;
              return (
                <MenuItem
                  key={child.title}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (child.path) {
                      navigate(child.path);
                      handlePopoverClose();
                    }
                  }}
                  selected={isActive}
                  sx={{
                    py: 1.5,
                    px: 2,
                    minWidth: 200,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: isActive ? 'primary.main' : 'inherit',
                    }}
                  >
                    {child.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={child.title}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive ? 600 : 500,
                    }}
                  />
                  {isActive && <ChevronRight fontSize="small" color="primary" />}
                </MenuItem>
              );
            })}
          </MenuList>
        )}
      </Popover>
    </>
  );
}

// 使用 memo 优化，避免父组件更新时不必要的重渲染
export default memo(Sidebar);
